import React from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditIcon from '@mui/icons-material/Edit';
import QuizIcon from '@mui/icons-material/Quiz';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface TopicSubtopicCardsProps {
  selectedSubject: string;
  selectedTopic: string;
  selectedSubtopic: string;
  availableSubjects: string[];
  availableTopics: string[];
  availableSubtopics: string[];
  onSubjectSelect: (subject: string) => void;
  onTopicSelect: (topic: string) => void;
  onSubtopicSelect: (subtopic: string) => void;
  onPredefinedQuestionClick: (questionData: any, type: 'doubt' | 'test') => void;
  onEditSubject?: () => void;
  onEditTopic?: () => void;
  onEditSubtopic?: () => void;
  filteredQuestions?: any[];

  // --- New: Board + Class step (superuser flow) ---
  isSuperuser?: boolean;
  boardOptions?: Array<{ label: string; value: string }>;
  selectedBoard?: string;
  onBoardSelect?: (board: string) => void;
  onEditBoard?: () => void;
  availableClasses?: string[];
  selectedClass?: string;
  onClassSelect?: (cls: string) => void;
  onEditClass?: () => void;

  // --- New: Organisation gate — no options show until this is satisfied ---
  organisationRequired?: boolean;
  organisationSelected?: boolean;
  selectedOrganisationLabel?: string;
}

const TOPIC_COLORS = [
  { bg: '#2196F3', border: '#2196F3', text: '#FFFFFF' },
  { bg: '#9C27B0', border: '#9C27B0', text: '#FFFFFF' },
  { bg: '#4CAF50', border: '#4CAF50', text: '#FFFFFF' },
  { bg: '#FF9800', border: '#FF9800', text: '#FFFFFF' },
  { bg: '#E91E63', border: '#E91E63', text: '#FFFFFF' },
  { bg: '#009688', border: '#009688', text: '#FFFFFF' },
  { bg: '#8BC34A', border: '#8BC34A', text: '#FFFFFF' },
  { bg: '#FF5722', border: '#FF5722', text: '#FFFFFF' },
];

const BREADCRUMB_COLORS: Record<string, string> = {
  organisation: '#795548',
  board: '#3F51B5',
  class: '#009688',
  subject: '#2196F3',
  topic: '#9C27B0',
  subtopic: '#4CAF50',
};

const scrollBoxSx = (maxHeight: string) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
  justifyContent: { xs: 'center', sm: 'flex-start' },
  overflowY: 'auto',
  maxHeight,
  pr: 1,
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
});

const optionChipSx = (color: { bg: string; border: string; text: string }) => ({
  cursor: 'pointer',
  bgcolor: color.bg,
  color: color.text,
  border: `2px solid ${color.border}`,
  fontSize: { xs: '12px', md: '13px' },
  height: { xs: '32px', md: '36px' },
  px: 1.5,
  py: 2,
  transition: 'all 0.3s ease',
  '&:hover': {
    bgcolor: color.border,
    color: '#fff',
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${color.border}60`,
  },
  '& .MuiChip-label': {
    px: 1,
  },
});

interface BreadcrumbItem {
  key: string;
  label: string;
  colorKey: keyof typeof BREADCRUMB_COLORS;
  onEdit?: () => void;
  editTitle?: string;
}

const TopicSubtopicCards: React.FC<TopicSubtopicCardsProps> = ({
  selectedSubject,
  selectedTopic,
  selectedSubtopic,
  availableSubjects,
  availableTopics,
  availableSubtopics,
  onSubjectSelect,
  onTopicSelect,
  onSubtopicSelect,
  onPredefinedQuestionClick,
  onEditSubject,
  onEditTopic,
  onEditSubtopic,
  filteredQuestions = [],
  isSuperuser = false,
  boardOptions = [],
  selectedBoard = '',
  onBoardSelect,
  onEditBoard,
  availableClasses = [],
  selectedClass = '',
  onClassSelect,
  onEditClass,
  organisationRequired = false,
  organisationSelected = false,
  selectedOrganisationLabel = '',
}) => {
  // Breadcrumb renderer — shows already-selected steps with an edit icon
  const renderBreadcrumb = (items: BreadcrumbItem[]) => (
    <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {items.map((item) => (
        <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={item.label}
            sx={{
              bgcolor: BREADCRUMB_COLORS[item.colorKey],
              color: '#fff',
              fontWeight: 600,
              fontSize: { xs: '11px', md: '13px' },
              height: { xs: '26px', md: '30px' },
            }}
          />
          {item.onEdit && (
            <IconButton
              size="small"
              onClick={item.onEdit}
              sx={{
                bgcolor: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                width: { xs: 24, md: 28 },
                height: { xs: 24, md: 28 },
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
              title={item.editTitle}
            >
              <EditIcon sx={{ fontSize: { xs: 12, md: 14 } }} />
            </IconButton>
          )}
        </Box>
      ))}
    </Box>
  );

  const renderOptionsGrid = (
    options: string[],
    onSelect: (v: string) => void,
    maxHeight: string,
    colorOffset = 0
  ) => (
    <Box sx={scrollBoxSx(maxHeight)}>
      {options.map((opt, index) => {
        const color = TOPIC_COLORS[(index + colorOffset) % TOPIC_COLORS.length];
        return (
          <Chip
            key={opt}
            label={opt}
            onClick={() => onSelect(opt)}
            sx={optionChipSx(color)}
          />
        );
      })}
    </Box>
  );

  const wrapperSx = {
    p: { xs: 1.5, md: 2 },
    maxWidth: '100%',
    overflow: 'hidden',
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  } as const;

  const titleSx = {
    mb: 1.5,
    color: '#2c3e50',
    fontSize: { xs: '1rem', md: '1.1rem' },
    flexShrink: 0,
  } as const;

  // ---------------------------------------------------------------------
  // Step -1: Organisation gate — nothing else renders until this passes
  // ---------------------------------------------------------------------
  if (organisationRequired && !organisationSelected) {
    return (
      <Box sx={wrapperSx}>
        <Typography variant="h6" fontWeight={600} sx={titleSx}>
          Select an Organisation
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '12px', md: '13px' } }}>
          Please select an Organisation from the sidebar to continue.
        </Typography>
      </Box>
    );
  }

  // ---------------------------------------------------------------------
  // Step 0: Board — only for superuser flow, before anything else
  // ---------------------------------------------------------------------
  if (isSuperuser && !selectedBoard) {
    return (
      <Box sx={wrapperSx}>
        <Typography variant="h6" fontWeight={600} sx={titleSx}>
          Select a Board
        </Typography>
        {boardOptions.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '12px', md: '13px' } }}>
            No boards found. Please select an Organisation first.
          </Typography>
        ) : (
          renderOptionsGrid(
            boardOptions.map((b) => b.label),
            (label) => {
              const found = boardOptions.find((b) => b.label === label);
              onBoardSelect?.(found ? found.value : label);
            },
            'calc(100vh - 300px)'
          )
        )}
      </Box>
    );
  }

  // ---------------------------------------------------------------------
  // Step 1: Class — after Board is selected
  // ---------------------------------------------------------------------
  if (isSuperuser && selectedBoard && !selectedClass) {
    return (
      <Box sx={wrapperSx}>
        {renderBreadcrumb([
          { key: 'board', label: `Board ${selectedBoard}`, colorKey: 'board', onEdit: onEditBoard, editTitle: 'Edit Board' },
        ])}

        <Typography variant="h6" fontWeight={600} sx={titleSx}>
          Select a Class
        </Typography>

        {availableClasses.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '12px', md: '13px' } }}>
            No classes found for this board.
          </Typography>
        ) : (
          renderOptionsGrid(
            availableClasses.map((c) => `Class ${c}`),
            (label) => {
              const cls = label.replace(/^Class\s/, '');
              onClassSelect?.(cls);
            },
            'calc(100vh - 350px)',
            1
          )
        )}
      </Box>
    );
  }

  // Common breadcrumb items for Organisation + Board + Class (shown on all steps below, superuser only)
  const boardClassBreadcrumb: BreadcrumbItem[] = isSuperuser
    ? [
      ...(organisationRequired && organisationSelected && selectedOrganisationLabel
        ? [{ key: 'organisation', label: selectedOrganisationLabel, colorKey: 'organisation' as const }]
        : []),
      { key: 'board', label: `Board ${selectedBoard}`, colorKey: 'board', onEdit: onEditBoard, editTitle: 'Edit Board' },
      { key: 'class', label: `Class ${selectedClass}`, colorKey: 'class', onEdit: onEditClass, editTitle: 'Edit Class' },
    ]
    : [];

  // ---------------------------------------------------------------------
  // Step 2: Subject — when no subject is selected
  // ---------------------------------------------------------------------
  if (!selectedSubject) {
    return (
      <Box sx={wrapperSx}>
        {boardClassBreadcrumb.length > 0 && renderBreadcrumb(boardClassBreadcrumb)}

        <Typography variant="h6" fontWeight={600} sx={titleSx}>
          Select a Subject
        </Typography>

        {availableSubjects.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '12px', md: '13px' } }}>
            No subjects found for this class.
          </Typography>
        ) : (
          renderOptionsGrid(availableSubjects, onSubjectSelect, 'calc(100vh - 300px)')
        )}
      </Box>
    );
  }

  // ---------------------------------------------------------------------
  // Step 3: Topic — when only subject is selected
  // ---------------------------------------------------------------------
  if (selectedSubject && !selectedTopic) {
    return (
      <Box sx={wrapperSx}>
        {renderBreadcrumb([
          ...boardClassBreadcrumb,
          { key: 'subject', label: selectedSubject, colorKey: 'subject', onEdit: onEditSubject, editTitle: 'Edit Subject' },
        ])}

        <Typography variant="h6" fontWeight={600} sx={titleSx}>
          Select a Topic in {selectedSubject}
        </Typography>

        {availableTopics.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '12px', md: '13px' } }}>
            No topics found for this subject.
          </Typography>
        ) : (
          renderOptionsGrid(availableTopics, onTopicSelect, 'calc(100vh - 350px)')
        )}
      </Box>
    );
  }

  // ---------------------------------------------------------------------
  // Step 4: Subtopic — when topic is selected but not subtopic
  // ---------------------------------------------------------------------
  if (selectedSubject && selectedTopic && !selectedSubtopic) {
    return (
      <Box sx={wrapperSx}>
        {renderBreadcrumb([
          ...boardClassBreadcrumb,
          { key: 'subject', label: selectedSubject, colorKey: 'subject', onEdit: onEditSubject, editTitle: 'Edit Subject' },
          { key: 'topic', label: selectedTopic, colorKey: 'topic', onEdit: onEditTopic, editTitle: 'Edit Topic' },
        ])}

        <Box sx={{ flexShrink: 0, mb: 1.5 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, color: '#2c3e50', fontSize: { xs: '1rem', md: '1.1rem' } }}>
            Select a Subtopic
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '11px', md: '13px' } }}>
            Choose from the available subtopics below
          </Typography>
        </Box>

        <Box sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 380px)', pr: 1 }}>
          {availableSubtopics.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#444', fontSize: { xs: '12px', md: '13px' } }}>
                📚 Subtopics
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, maxWidth: '100%' }}>
                {availableSubtopics.map((subtopic, index) => {
                  const color = TOPIC_COLORS[(index + 2) % TOPIC_COLORS.length];
                  return (
                    <Chip
                      key={subtopic}
                      label={subtopic}
                      onClick={() => onSubtopicSelect(subtopic)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: color.bg,
                        color: color.text,
                        border: `1.5px solid ${color.border}`,
                        fontSize: { xs: '11px', md: '12px' },
                        height: { xs: '28px', md: '32px' },
                        px: 1.25,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: color.border,
                          color: '#fff',
                          transform: 'translateY(-2px)',
                          boxShadow: `0 3px 6px ${color.border}60`,
                        },
                        '& .MuiChip-label': {
                          px: 0.75,
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // ---------------------------------------------------------------------
  // Step 5: Questions — when all of board/class/subject/topic/subtopic are selected
  // ---------------------------------------------------------------------
  if (selectedSubject && selectedTopic && selectedSubtopic) {
    return (
      <Box sx={wrapperSx}>
        {renderBreadcrumb([
          ...boardClassBreadcrumb,
          { key: 'subject', label: selectedSubject, colorKey: 'subject', onEdit: onEditSubject, editTitle: 'Edit Subject' },
          { key: 'topic', label: selectedTopic, colorKey: 'topic', onEdit: onEditTopic, editTitle: 'Edit Topic' },
          { key: 'subtopic', label: selectedSubtopic, colorKey: 'subtopic', onEdit: onEditSubtopic, editTitle: 'Edit Subtopic' },
        ])}

        {/* Questions Section */}
        <Box sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 320px)', pr: 1 }}>
          {filteredQuestions && filteredQuestions.length > 0 ? (
            <>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: '#444', fontSize: { xs: '13px', md: '14px' } }}>
                💡 Suggested Questions
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredQuestions.map((item, index) => {
                  const questionText = item?.entity?.question || item?.question || 'Question not available';
                  const isDoubt = true;

                  return (
                    <Chip
                      key={index}
                      icon={isDoubt ? <HelpOutlineIcon /> : <QuizIcon />}
                      label={questionText}
                      onClick={() => onPredefinedQuestionClick(item, 'doubt')}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isDoubt ? '#FFF3E0' : '#E3F2FD',
                        color: isDoubt ? '#E65100' : '#1565C0',
                        border: `1.5px solid ${isDoubt ? '#FF9800' : '#2196F3'}`,
                        fontSize: { xs: '12px', md: '13px' },
                        height: 'auto',
                        minHeight: { xs: '36px', md: '40px' },
                        px: 1.5,
                        py: 1,
                        justifyContent: 'flex-start',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: isDoubt ? '#FFE0B2' : '#BBDEFB',
                          transform: 'translateX(4px)',
                          boxShadow: `0 2px 8px ${isDoubt ? '#FF980040' : '#2196F340'}`,
                        },
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          textAlign: 'left',
                          py: 0.5,
                          display: 'block',
                          width: '100%',
                        },
                        '& .MuiChip-icon': {
                          fontSize: { xs: 16, md: 18 },
                          color: isDoubt ? '#FF9800' : '#2196F3',
                          marginLeft: 0.5,
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </>
          ) : null}
        </Box>

        {/* Call to Action */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            gap: 1,
            mt: 2,
            bgcolor: '#E3F2FD',
            p: { xs: 1.5, md: 2 },
            borderRadius: 2,
            border: '2px dashed #2196F3',
          }}
        >
          <ChatBubbleOutlineIcon sx={{ color: '#2196F3', fontSize: { xs: 18, md: 22 } }} />
          <Typography
            variant="body2"
            sx={{
              color: '#1565C0',
              fontWeight: 600,
              fontSize: { xs: '12px', md: '13px' },
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            Or ask your own question below
          </Typography>
        </Box>
      </Box>
    );
  }

  return null;
};

export default TopicSubtopicCards;